import { useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useCustomerUsers } from 'services/assist-api/entities/customer-user/customer-users';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import type { TeacherAuthorisedMark } from 'services/assist-api/entities/test-instance-response-mark/types';

/**
 * Fetches the organisation users who are active markers of the specified marks, ensuring that
 * the same organisation user isn't fetched twice.
 */
export default function useActiveMarkerOrganisationUsers(
  marksQuery: UseQueryResult<RequestResponse<TeacherAuthorisedMark[]>>
): UseQueryResult<RequestResponse<BaseCustomerUser[]>, RequestError> {
  // Get all organisation users who are active markers of these marks only.
  const organisationUserIds = useMemo(
    () => [
      ...new Set(
        (marksQuery.data?.json || [])
          .map((mark) => mark.activeMarker?.user.id)
          .filter(
            (organisationUserId): organisationUserId is number => organisationUserId !== undefined
          )
      )
    ],
    [marksQuery.data?.json]
  );

  return useCustomerUsers<BaseCustomerUser>(
    {
      filters: { id: organisationUserIds },
      groups: ['id']
    },
    { enabled: marksQuery.isSuccess && organisationUserIds.length > 0 }
  );
}
